import { createSlice, nanoid } from '@reduxjs/toolkit';

// Trip status constants
export const TRIP_STATUS = {
  REQUESTED: 'requested',
  ASSIGNED: 'assigned',
  ONGOING: 'ongoing',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

// Trip type constants
export const TRIP_TYPE = {
  STANDARD: 'standard',
  PREMIUM: 'premium',
  SHARED: 'shared',
  SCHEDULED: 'scheduled'
};

// Generate realistic dummy trip data
const generateDummyTrips = () => {
  const passengers = [
    { id: 'p1', name: 'Ollie Nora', phone: '+1-555-0101', avatar: '/images/p1.jpg' },
    { id: 'p2', name: 'Sarah Johnson', phone: '+1-555-0102', avatar: '/images/p2.jpg' },
    { id: 'p3', name: 'Mike Chen', phone: '+1-555-0103', avatar: '/images/p3.jpg' },
    { id: 'p4', name: 'Emma Wilson', phone: '+1-555-0104', avatar: '/images/p4.jpg' },
    { id: 'p5', name: 'David Brown', phone: '+1-555-0105', avatar: '/images/p5.jpg' }
  ];

  const drivers = [
    { id: 'd1', name: 'Jon Williams', phone: '+1-555-0201', rating: 4.8, vehicle: 'Volvo XC40' },
    { id: 'd2', name: 'Alex Rodriguez', phone: '+1-555-0202', rating: 4.6, vehicle: 'Toyota Camry' },
    { id: 'd3', name: 'Lisa Thompson', phone: '+1-555-0203', rating: 4.9, vehicle: 'Honda Civic' },
    { id: 'd4', name: 'Robert Davis', phone: '+1-555-0204', rating: 4.7, vehicle: 'Ford Focus' },
    { id: 'd5', name: 'Maria Garcia', phone: '+1-555-0205', rating: 4.5, vehicle: 'Tesla Model 3' }
  ];

  const locations = [
    { name: 'Downtown Mall', address: '123 Main St, Downtown', lat: 40.7128, lng: -74.0060 },
    { name: 'Airport Terminal', address: '456 Airport Rd, Terminal 1', lat: 40.6892, lng: -74.1745 },
    { name: 'Central Station', address: '789 Station Ave, Central', lat: 40.7505, lng: -73.9934 },
    { name: 'Business District', address: '321 Corporate Blvd, Business', lat: 40.7589, lng: -73.9851 },
    { name: 'University Campus', address: '654 College St, Campus', lat: 40.8075, lng: -73.9626 }
  ];

  const trips = [];
  const statuses = Object.values(TRIP_STATUS);
  const types = Object.values(TRIP_TYPE);

  for (let i = 1; i <= 50; i++) {
    const passenger = passengers[Math.floor(Math.random() * passengers.length)];
    const driver = drivers[Math.floor(Math.random() * drivers.length)];
    const pickup = locations[Math.floor(Math.random() * locations.length)];
    const dropoff = locations[Math.floor(Math.random() * locations.length)];
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const type = types[Math.floor(Math.random() * types.length)];

    const baseDate = new Date();
    baseDate.setDate(baseDate.getDate() - Math.floor(Math.random() * 30));

    const distance = (Math.random() * 20 + 2).toFixed(1); // 2-22 km
    const duration = Math.floor(Math.random() * 45 + 10); // 10-55 minutes
    const baseFare = parseFloat((distance * 2.5 + duration * 0.5).toFixed(2));
    const surge = status === TRIP_STATUS.COMPLETED ? (Math.random() * 0.5 + 1).toFixed(1) : 1.0;
    const totalFare = (baseFare * surge).toFixed(2);

    trips.push({
      id: nanoid(),
      passenger: {
        id: passenger.id,
        name: passenger.name,
        phone: passenger.phone,
        avatar: passenger.avatar
      },
      driver: status !== TRIP_STATUS.REQUESTED ? {
        id: driver.id,
        name: driver.name,
        phone: driver.phone,
        rating: driver.rating,
        vehicle: driver.vehicle
      } : null,
      pickup: {
        name: pickup.name,
        address: pickup.address,
        coordinates: { lat: pickup.lat, lng: pickup.lng }
      },
      dropoff: {
        name: dropoff.name,
        address: dropoff.address,
        coordinates: { lat: dropoff.lat, lng: dropoff.lng }
      },
      status,
      type,
      requestedAt: baseDate.toISOString(),
      assignedAt: status !== TRIP_STATUS.REQUESTED ? new Date(baseDate.getTime() + 2 * 60000).toISOString() : null,
      startedAt: [TRIP_STATUS.ONGOING, TRIP_STATUS.COMPLETED].includes(status) ?
        new Date(baseDate.getTime() + 5 * 60000).toISOString() : null,
      completedAt: status === TRIP_STATUS.COMPLETED ?
        new Date(baseDate.getTime() + (duration + 5) * 60000).toISOString() : null,
      cancelledAt: status === TRIP_STATUS.CANCELLED ?
        new Date(baseDate.getTime() + 3 * 60000).toISOString() : null,
      distance: parseFloat(distance),
      estimatedDuration: duration,
      actualDuration: status === TRIP_STATUS.COMPLETED ? duration + Math.floor(Math.random() * 10 - 5) : null,
      fare: {
        base: baseFare,
        surge: parseFloat(surge),
        total: parseFloat(totalFare),
        currency: 'USD'
      },
      payment: {
        method: ['card', 'cash', 'wallet'][Math.floor(Math.random() * 3)],
        status: status === TRIP_STATUS.COMPLETED ? 'completed' : 'pending'
      },
      rating: status === TRIP_STATUS.COMPLETED ? {
        passenger: Math.floor(Math.random() * 2) + 4, // 4-5 stars
        driver: Math.floor(Math.random() * 2) + 4 // 4-5 stars
      } : null,
      notes: Math.random() > 0.7 ? 'Special instructions provided' : null
    });
  }

  return trips.sort((a, b) => new Date(b.requestedAt) - new Date(a.requestedAt));
};

const initialState = {
  trips: generateDummyTrips(),
  isLoading: false,
  error: null,
  filters: {
    status: 'all',
    type: 'all',
    dateRange: 'all',
    searchTerm: ''
  },
  selectedTrip: null
};

const tripSlice = createSlice({
  name: 'trips',
  initialState,
  reducers: {
    // Loading and error states
    setLoading(state, action) {
      state.isLoading = action.payload;
    },

    setError(state, action) {
      state.error = action.payload;
      state.isLoading = false;
    },

    clearError(state) {
      state.error = null;
    },

    // Filter actions
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },

    clearFilters(state) {
      state.filters = {
        status: 'all',
        type: 'all',
        dateRange: 'all',
        searchTerm: ''
      };
    },

    // Selected trip actions
    setSelectedTrip(state, action) {
      state.selectedTrip = action.payload;
    },

    clearSelectedTrip(state) {
      state.selectedTrip = null;
    },
    createTrip: {
      reducer(state, action) {
        state.trips.unshift(action.payload);
      },
      prepare(tripData) {
        return {
          payload: {
            id: nanoid(),
            ...tripData,
            status: TRIP_STATUS.REQUESTED,
            requestedAt: new Date().toISOString(),
            assignedAt: null,
            startedAt: null,
            completedAt: null,
            cancelledAt: null,
            driver: null,
            actualDuration: null,
            rating: null
          }
        };
      }
    },

    assignDriver(state, action) {
      const { tripId, driver } = action.payload;
      const trip = state.trips.find(t => t.id === tripId);
      if (trip && trip.status === TRIP_STATUS.REQUESTED) {
        trip.driver = driver;
        trip.status = TRIP_STATUS.ASSIGNED;
        trip.assignedAt = new Date().toISOString();
      }
    },

    startTrip(state, action) {
      const { tripId } = action.payload;
      const trip = state.trips.find(t => t.id === tripId);
      if (trip && trip.status === TRIP_STATUS.ASSIGNED) {
        trip.status = TRIP_STATUS.ONGOING;
        trip.startedAt = new Date().toISOString();
      }
    },

    completeTrip(state, action) {
      const { tripId, actualDuration, finalFare } = action.payload;
      const trip = state.trips.find(t => t.id === tripId);
      if (trip && trip.status === TRIP_STATUS.ONGOING) {
        trip.status = TRIP_STATUS.COMPLETED;
        trip.completedAt = new Date().toISOString();
        trip.actualDuration = actualDuration || trip.estimatedDuration;
        if (finalFare) {
          trip.fare.total = finalFare;
        }
        trip.payment.status = 'completed';
      }
    },

    cancelTrip(state, action) {
      const { tripId, reason } = action.payload;
      const trip = state.trips.find(t => t.id === tripId);
      if (trip && [TRIP_STATUS.REQUESTED, TRIP_STATUS.ASSIGNED].includes(trip.status)) {
        trip.status = TRIP_STATUS.CANCELLED;
        trip.cancelledAt = new Date().toISOString();
        trip.cancellationReason = reason;
      }
    },

    updateTripLocation(state, action) {
      const { tripId, currentLocation } = action.payload;
      const trip = state.trips.find(t => t.id === tripId);
      if (trip && trip.status === TRIP_STATUS.ONGOING) {
        trip.currentLocation = currentLocation;
        trip.lastLocationUpdate = new Date().toISOString();
      }
    },

    addTripRating(state, action) {
      const { tripId, rating } = action.payload;
      const trip = state.trips.find(t => t.id === tripId);
      if (trip && trip.status === TRIP_STATUS.COMPLETED) {
        trip.rating = { ...trip.rating, ...rating };
      }
    },

    updateTrip(state, action) {
      const { id, ...updates } = action.payload;
      const tripIndex = state.trips.findIndex(t => t.id === id);
      if (tripIndex !== -1) {
        state.trips[tripIndex] = { ...state.trips[tripIndex], ...updates };
      }
    },

    deleteTrip(state, action) {
      state.trips = state.trips.filter(trip => trip.id !== action.payload);
    }
  }
});

export const {
  setLoading,
  setError,
  clearError,
  setFilters,
  clearFilters,
  setSelectedTrip,
  clearSelectedTrip,
  createTrip,
  assignDriver,
  startTrip,
  completeTrip,
  cancelTrip,
  updateTripLocation,
  addTripRating,
  updateTrip,
  deleteTrip
} = tripSlice.actions;

export default tripSlice.reducer;